import { useEffect, useState } from "react";
import { navItems } from "@/mocks/siteData";

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>("directions");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = navItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleGo = (id: string) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2.5 cursor-pointer shrink-0"
            aria-label="返回页面顶部"
          >
            <span
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                scrolled ? "bg-primary-500 text-background-50" : "bg-background-50/15 text-background-50"
              }`}
            >
              <i className="ri-planet-line text-xl" aria-hidden="true"></i>
            </span>
            <span
              className={`font-heading text-lg md:text-xl font-semibold tracking-wide transition-colors ${
                scrolled ? "text-foreground-950" : "text-background-50"
              }`}
            >
              星辰影院
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-0.5" aria-label="页面分区导航">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleGo(item.id)}
                className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  active === item.id
                    ? scrolled
                      ? "text-primary-600 font-medium"
                      : "text-accent-300 font-medium"
                    : scrolled
                    ? "text-foreground-600 hover:text-foreground-950"
                    : "text-background-100/85 hover:text-background-50"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleGo("library")}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-base" aria-hidden="true"></i>
              开始查看片单
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                scrolled ? "text-foreground-900 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
              }`}
              aria-label={open ? "关闭菜单" : "打开菜单"}
              aria-expanded={open}
            >
              <i className={open ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-background-50 border-b border-background-200">
          <div className="mx-auto max-w-7xl px-5 md:px-8 py-3 grid grid-cols-2 gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleGo(item.id)}
                className="text-left px-3 py-2.5 text-sm rounded-md text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}